import React, { useMemo, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { toast } from "react-toastify";
import RunPanel from "./RunPanel";
import RunPicker from "./RunPicker";
import { zb, inr } from "./api";
import styles from "./ZohoBilling.module.css";

function GoogleBody({ run }) {
  const data = run.result || run.preview;
  if (!data) return null;
  return (
    <>
      <div className={styles.sectionLabel}>{run.result ? "Posting result" : "Preview — nothing has been posted"}</div>
      <div className={styles.muted}>
        {Object.entries(data.counts || {})
          .map(([k, v]) => `${k} ${v}`)
          .join(" · ")}
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>File</th>
              <th>Status</th>
              <th>Invoice</th>
              <th>Date</th>
              <th>Domain</th>
              <th>Total</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.file}>
                <td className={styles.small}>{r.file.replace(/^\d{3}_/, "")}</td>
                <td>{r.status}</td>
                <td className={styles.mono}>{r.invoiceNumber || "—"}</td>
                <td>{r.date || "—"}</td>
                <td>{r.domain || "—"}</td>
                <td>{inr(r.postedTotal != null ? r.postedTotal : r.total)}</td>
                <td className={styles.small}>{r.error || (r.roundOff ? `round-off ${r.roundOff}` : "—")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function GoogleTab({ runs, activeRunId, onChanged }) {
  const mine = useMemo(() => runs.filter((r) => r.kind === "google"), [runs]);
  const [selected, setSelected] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const current = selected || (mine[0] && mine[0].id);

  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      for (const f of files) form.append("files", f);
      const data = await zb("/google/upload", { method: "POST", form });
      toast.success(`${files.length} invoice(s) uploaded. Run the preview next.`);
      setFiles([]);
      setSelected(data.run.id);
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardTitle}>Upload Google Workspace invoice PDFs</div>
        <div className={styles.muted}>
          Each invoice becomes a bill under GOOGLE INDIA PRIVATE LIMTED (Business Support Services, IGST 18%, HSN 998319)
          with the PDF attached. Invoices already in Zoho are skipped.
        </div>
        <div className={styles.uploadRow}>
          <label className={styles.fileBtn}>
            <UploadFileOutlinedIcon fontSize="small" />
            {files.length ? `${files.length} PDF(s) selected` : "Choose PDFs…"}
            <input type="file" accept=".pdf" multiple hidden onChange={(e) => setFiles([...e.target.files])} />
          </label>
          <Button className={styles.primaryBtn} variant="contained" disabled={!files.length || uploading} onClick={upload}>
            {uploading ? <CircularProgress size={16} color="inherit" /> : "Upload"}
          </Button>
        </div>
      </div>
      <RunPicker runs={mine} current={current} onSelect={setSelected} activeRunId={activeRunId} />
      {current && (
        <RunPanel
          key={current}
          runId={current}
          onChanged={onChanged}
          canPreview={() => true}
          post={(run) => {
            const n = (run.preview?.rows || []).filter((r) => r.status === "WOULD_POST").length;
            if (!n) return null;
            return {
              label: "Post to Zoho Books",
              title: `Post ${n} Google bill(s) to Zoho Books (live)?`,
              body: <p>Creates the bills under the Google vendor and attaches each PDF. Existing invoice numbers are skipped.</p>,
            };
          }}
        >
          {(run) => <GoogleBody run={run} />}
        </RunPanel>
      )}
    </>
  );
}
